import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Heart, Eye, Send, Sparkles, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * KidsCommunity — kid-safe community feed.
 *
 * Mirrors /community for grown-ups but only shows is_kids_safe content,
 * uses a playful gradient aesthetic, and hides all adult-only actions
 * (DMs, reporting flow, ad tools). Signed-in kid creators can drop a
 * quick post using the compact `kids-community-composer`.
 */
export default function KidsCommunity() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [emoji, setEmoji] = useState("⭐");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/kids-public/community?limit=60");
      setPosts(r.data?.posts || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const canPost = user && (user.is_kids_safe || user.is_kid_bot || user.role === "admin" || user.controlled_by_parent_id);

  const submit = async () => {
    if (!draft.trim()) { toast.error("Say something fun!"); return; }
    setPosting(true);
    try {
      await api.post("/kids-public/posts", { title: draft.trim(), emoji });
      setDraft("");
      toast.success("Posted! 🎉");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPosting(false); }
  };

  return (
    <div data-testid="kids-community-page" className="pb-20 px-3 sm:px-6 md:px-10 max-w-5xl mx-auto text-white">
      <Link to="/kids" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-3"><ArrowLeft className="w-4 h-4" /> Back to Kids</Link>
      <div className="relative overflow-hidden rounded-3xl p-5 sm:p-8 bg-gradient-to-br from-pink-400 via-fuchsia-500 to-sky-500 shadow-2xl ring-4 ring-white/25">
        <div className="flex items-center gap-3 flex-wrap">
          <img src="/kids-learn-play-logo.png" alt="" className="w-20 sm:w-28 h-auto drop-shadow-2xl" draggable={false} />
          <div>
            <div className="inline-flex items-center gap-1 bg-white/20 text-white text-[10px] uppercase tracking-[0.25em] font-black px-2 py-0.5 rounded-full backdrop-blur"><Sparkles className="w-3 h-3" /> Kids Community</div>
            <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter drop-shadow mt-1">Say hi!</h1>
            <p className="text-white/90 text-sm">Share what you made, what you learned, or just a wave. Safe & kid-only.</p>
          </div>
        </div>
      </div>

      {canPost && (
        <div data-testid="kids-community-composer" className="mt-5 bg-black/30 border border-white/10 rounded-2xl p-4 flex flex-col sm:flex-row gap-3 items-stretch">
          <input
            data-testid="kcom-emoji"
            value={emoji}
            onChange={(e) => setEmoji(e.target.value.slice(0, 4))}
            className="w-16 bg-white/10 border border-white/20 rounded-lg text-2xl text-center"
            placeholder="⭐"
          />
          <input
            data-testid="kcom-draft"
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 280))}
            placeholder="What's on your mind? (kid-safe only!)"
            className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white outline-none"
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
          <button
            data-testid="kcom-post-btn"
            onClick={submit}
            disabled={posting}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white font-black px-5 py-2 rounded-lg disabled:opacity-60"
          ><Send className="w-4 h-4" /> {posting ? "Posting…" : "Post"}</button>
        </div>
      )}

      {loading && <div className="mt-8 text-white/60 text-sm text-center">Loading…</div>}
      {!loading && posts.length === 0 && (
        <div data-testid="kcom-empty" className="mt-8 bg-black/30 border border-dashed border-white/10 rounded-2xl p-8 text-center text-white/70">
          <div className="text-5xl mb-2">🎨</div>
          No posts yet — be the first to say hi!
        </div>
      )}
      <div data-testid="kcom-grid" className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {posts.map((p) => (
          <div
            key={p.id}
            data-testid={`kcom-post-${p.id}`}
            className={`rounded-2xl p-4 bg-gradient-to-br ${p.color || "from-pink-400 to-fuchsia-500"} shadow-lg ring-2 ring-white/25 text-white relative overflow-hidden`}
          >
            <div className="text-[3rem] leading-none drop-shadow">{p.emoji || "⭐"}</div>
            <div className="font-display font-black text-lg mt-1 leading-snug drop-shadow">{p.title}</div>
            <div className="mt-3 flex items-center gap-2 text-xs font-bold text-white/90">
              <Link to={`/kids/timeline/${p.creator_id}`} className="flex items-center gap-2 hover:underline">
                <span className="w-6 h-6 rounded-full bg-white/25 flex items-center justify-center text-sm">{p.creator_avatar || "⭐"}</span>
                @{p.handle || p.creator_name}
              </Link>
              <span className="ml-auto inline-flex items-center gap-3">
                {p.like_count > 0 && <span className="inline-flex items-center gap-0.5"><Heart className="w-3 h-3" /> {p.like_count}</span>}
                {p.view_count > 0 && <span className="inline-flex items-center gap-0.5"><Eye className="w-3 h-3" /> {p.view_count}</span>}
                {p.comment_count > 0 && <span className="inline-flex items-center gap-0.5"><MessageCircle className="w-3 h-3" /> {p.comment_count}</span>}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
